import React, { useMemo } from 'react';
import { Card, Stack, Group, Text, Badge, Box } from '@mantine/core';

// Risk priority colors consistent with RiskLandscape.js
const riskPriorityColors = {
  Critical: '#1A1A1A',    // Black
  High: '#993333',        // Red
  Significant: '#B94D4D', // Amber
  Moderate: '#D4AA7D',    // Light Orange
  Low: '#B8C2B7',         // Blue
  Negligible: '#D8D8D8',  // Green
  None: '#EBEBEB'         // Light Grey
};

const getRiskColor = (rating) => {
  const capitalizedRating = rating?.charAt(0).toUpperCase() + rating?.slice(1).toLowerCase();
  return riskPriorityColors[capitalizedRating] || '#EBEBEB';
};

/**
 * ResidualRiskCards - Reusable component for displaying residual risk assessments
 *
 * Shows risks with their post-mitigation residual ratings after controls are implemented
 * Used in both AutopilotAssessmentFlow summary and Canvas Overview tab
 */
export default function ResidualRiskCards({ riskFormData, residualRiskState, savedControls }) {
  // Get all risks with residual assessments
  const risksWithResidual = useMemo(() => {
    const results = [];

    Object.entries(riskFormData || {}).forEach(([areaName, areaData]) => {
      const areaRisks = areaData?.areaRisks || {};
      Object.entries(areaRisks).forEach(([sourceName, risks]) => {
        if (Array.isArray(risks)) {
          risks.forEach(risk => {
            if (risk.status === 'Saved' && residualRiskState?.[risk.riskId]) {
              const residualData = residualRiskState[risk.riskId];
              const controls = savedControls?.[risk.riskId] || [];
              results.push({
                ...risk,
                areaName,
                sourceName,
                controls: controls.length,
                residualData
              });
            }
          });
        }
      });
    });

    return results;
  }, [riskFormData, residualRiskState, savedControls]);

  if (risksWithResidual.length === 0) {
    return (
      <Card withBorder p="xl">
        <Stack align="center" gap="md">
          <Text size="lg" c="dimmed">No Residual Risk Assessments Available</Text>
          <Text size="sm" c="dimmed" ta="center">
            Residual risk assessments will appear here after completing risk evaluations,
            implementing action plans, and running residual risk analysis.
          </Text>
        </Stack>
      </Card>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1rem'
    }}>
      {risksWithResidual.map((risk) => {
        // Handle null ratings gracefully - show "N/A" for missing/failed assessments
        const residualRating = risk.residualData.residual_rating || 'N/A';
        const actionPlanRating = risk.residualData.action_plan_rating || 'N/A';

        return (
          <Card key={risk.riskId} withBorder p="md" bg="gray.0">
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start">
                <div style={{ flex: 1 }}>
                  <Text fw={600} size="sm" lineClamp={2}>
                    {risk.riskName}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {risk.areaName} • {risk.sourceName}
                  </Text>
                </div>
                <Badge
                  style={{
                    backgroundColor: residualRating === 'N/A' ? '#9CA3AF' : getRiskColor(residualRating),
                    color: residualRating?.toLowerCase() === 'critical' ? 'white' : '#1A1A1A'
                  }}
                  variant="filled"
                  size="sm"
                >
                  {residualRating === 'N/A' ? 'N/A' : residualRating?.charAt(0).toUpperCase() + residualRating?.slice(1).toLowerCase()}
                </Badge>
              </Group>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '0.5rem'
              }}>
                <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                  <Text size="xs" fw={600} c="blue">
                    {risk.priority?.charAt(0).toUpperCase() + risk.priority?.slice(1).toLowerCase() || 'N/A'}
                  </Text>
                  <Text size="xs" c="dimmed">Initial</Text>
                </Box>
                <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                  <Text size="xs" fw={600} c="orange">{risk.controls}</Text>
                  <Text size="xs" c="dimmed">Actions</Text>
                </Box>
                <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                  <Text size="xs" fw={600} c="green">{actionPlanRating}</Text>
                  <Text size="xs" c="dimmed">Plan Rating</Text>
                </Box>
              </div>

              {/* Residual Explanation */}
              {risk.residualData.residual_explanation && (
                <Text size="xs" c="dimmed" style={{
                  backgroundColor: 'white',
                  padding: '8px',
                  borderRadius: '4px',
                  borderLeft: `3px solid ${getRiskColor(residualRating)}`
                }}>
                  <Text span fw={600} size="xs">Residual: </Text>
                  {risk.residualData.residual_explanation.length > 100
                    ? `${risk.residualData.residual_explanation.slice(0, 100)}...`
                    : risk.residualData.residual_explanation}
                </Text>
              )}

              {/* Action Plan Analysis */}
              {risk.residualData.action_plan_short_summary && (
                <Text size="xs" c="dimmed" style={{
                  backgroundColor: 'white',
                  padding: '8px',
                  borderRadius: '4px',
                  borderLeft: `3px solid ${
                    actionPlanRating === 'Excellent' ? '#2E7D32' :
                    actionPlanRating === 'Very Good' ? '#388E3C' :
                    actionPlanRating === 'Good' ? '#1976D2' :
                    actionPlanRating === 'Fair' ? '#F9A825' :
                    actionPlanRating === 'Poor' ? '#E65100' : '#C62828'
                  }`
                }}>
                  <Text span fw={600} size="xs">Plan: </Text>
                  {risk.residualData.action_plan_short_summary.length > 100
                    ? `${risk.residualData.action_plan_short_summary.slice(0, 100)}...`
                    : risk.residualData.action_plan_short_summary}
                </Text>
              )}
            </Stack>
          </Card>
        );
      })}
    </div>
  );
}
